import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "gatsby";
import cx from "classnames";
import { useLocation } from "@reach/router";

import { Menu, Close, Logo, MapleLeaf } from "@components/Icon";
import TryMikaButton from "@components/TryMikaButton";
import Button from "@components/Button";
import NavMenuItem from "./NavMenuItem";

import { MENU_CONFIG } from "./menuConfig";

import styles from "./index.module.scss";

const APP_URL = process.env.GATSBY_CLINIC_APP_URL || "https://app.mikatahealth.com";

interface NavbarProps {
  isScrolled?: boolean;
  className?: string;
}

const Navbar: React.FC<NavbarProps> = ({ isScrolled, className = "" }) => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileMenuHeight, setMobileMenuHeight] = useState("100%");
  const [bannerVisibilityChanged, setBannerVisibilityChanged] = useState(false);
  const navbarRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const [backgroundColor, setBackgroundColor] = useState("#003125");
  const location = useLocation();

  const [inviteCode, setInviteCode] = useState<string>();
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const codeParam = params.get("code");
      if (codeParam) {
        window.sessionStorage.setItem("scribeInviteCode", codeParam);
        setInviteCode(codeParam);
      }
    }
  }, []);

  // Memoize the calculation function so we can call it from multiple places
  const calculateMenuHeight = useCallback(() => {
    const headerElement = document.getElementById("header");
    if (headerElement && mobileMenuRef.current) {
      const headerHeight = headerElement.offsetHeight;
      const windowHeight = window.innerHeight;

      // Take into account safe areas for mobile devices, especially Safari
      const safeAreaBottom = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue("--sat") || "0",
        10
      );

      // Set the height to fill the available space, accounting for the header and any safe areas
      const availableHeight = windowHeight - headerHeight - safeAreaBottom;
      setMobileMenuHeight(`${availableHeight}px`);
    }
  }, []);

  // Calculate the header height including banner
  useEffect(() => {
    // Calculate on mount and whenever dependencies change
    calculateMenuHeight();

    // Also recalculate on window resize and orientation change
    window.addEventListener("resize", calculateMenuHeight);
    window.addEventListener("orientationchange", calculateMenuHeight);

    return () => {
      window.removeEventListener("resize", calculateMenuHeight);
      window.removeEventListener("orientationchange", calculateMenuHeight);
    };
  }, [calculateMenuHeight, isMobileMenuOpen, bannerVisibilityChanged]);

  useEffect(() => {
    const handleBannerClosed = () => {
      // Force recalculation even if the mobile menu is already open
      setBannerVisibilityChanged((prev) => !prev); // Toggle to force re-render

      // Also directly update the height immediately
      setTimeout(calculateMenuHeight, 0);
    };

    window.addEventListener("bannerClosed", handleBannerClosed);

    return () => {
      window.removeEventListener("bannerClosed", handleBannerClosed);
    };
  }, [calculateMenuHeight]);

  // Setup CSS variables for iOS safe areas
  useEffect(() => {
    // Function to update CSS variables for safe areas
    const updateSafeAreaVariables = () => {
      // Get computed style for the safe area insets
      const computedStyle = window.getComputedStyle(document.documentElement);

      // Set CSS variables for the safe area insets if they exist
      document.documentElement.style.setProperty(
        "--sat",
        computedStyle.getPropertyValue("env(safe-area-inset-bottom)") || "0px"
      );
    };

    // Update on mount
    updateSafeAreaVariables();

    // Update on resize and orientation change
    window.addEventListener("resize", updateSafeAreaVariables);
    window.addEventListener("orientationchange", updateSafeAreaVariables);

    return () => {
      window.removeEventListener("resize", updateSafeAreaVariables);
      window.removeEventListener("orientationchange", updateSafeAreaVariables);
    };
  }, []);

  useEffect(() => {
    if (location.pathname === "/mika-ai-medical-scribe/") {
      setBackgroundColor("#1d2029");
    } else if (location.pathname?.startsWith("/blog/")) {
      setBackgroundColor("#f1f7fd");
    } else if (location.pathname === "/who-we-serve/family-medicine/") {
      setBackgroundColor("#FEF0F1");
    } else if (location.pathname === "/who-we-serve/medical-imaging/") {
      setBackgroundColor("#E2FAF3");
    } else if (location.pathname === "/who-we-serve/specialty-medicine/") {
      setBackgroundColor("#F0F3FE");
    } else {
      setBackgroundColor("#003125");
    }
  }, [location.pathname]);

  const darkMode =
    isScrolled ||
    [
      "/who-we-serve/family-medicine/",
      "/who-we-serve/medical-imaging/",
      "/who-we-serve/specialty-medicine/"
    ].indexOf(location.pathname || "") !== -1 ||
    location.pathname?.startsWith("/blog/");

  return (
    <div
      ref={navbarRef}
      className={cx(styles.Navbar, className)}
      style={{
        background: isScrolled ? "white" : backgroundColor,
        borderBottom: isScrolled ? "1px solid #1D2029" : "none"
      }}
    >
      <div className={styles.NavContainer}>
        <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="logo">
          <Logo isWhiteBg={darkMode} />
        </Link>

        {/* Desktop Nav */}
        <div className={styles.NavItemsContainer}>
          <div className={styles.LinkItems}>
            {MENU_CONFIG.map((item) => (
              <NavMenuItem
                key={item.id}
                item={item}
                isScrolled={darkMode}
                openMenu={openMenu}
                setOpenMenu={setOpenMenu}
              />
            ))}
          </div>
          <div className={styles.ButtonItems}>
            <Button
              onClick={() => {
                window.location.href = APP_URL;
              }}
              variant="outline"
              isSmall
              dark={darkMode}
            >
              Login
            </Button>
            <TryMikaButton isSmall dark={darkMode} />
          </div>
        </div>

        {/* Mobile Menu Icon */}
        <div
          className={cx(styles.MobileMenuIcon, {
            [styles.MobileMenuIconDark]: darkMode
          })}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <Close /> : <Menu />}
        </div>
      </div>

      {isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className={cx(styles.MobileMenu, {
            [styles.MobileMenuDark]: darkMode
          })}
          style={{
            height: mobileMenuHeight,
            background: isScrolled ? "white" : backgroundColor
          }}
        >
          <div className={styles.MobileMenuContent}>
            <div className={styles.MobileMenuLinks}>
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={cx(styles.MobileMenuLink, {
                  [styles.MobileMenuLinkDark]: darkMode
                })}
              >
                Home
              </Link>
              {MENU_CONFIG.map((item) => (
                <div key={item.id} className={styles.MobileMenuItem}>
                  <Link
                    to={item.path || "#"}
                    className={cx(styles.MobileMenuLink, {
                      [styles.MobileMenuLinkSubpath]: item.subItems,
                      [styles.MobileMenuLinkDark]: darkMode
                    })}
                    onClick={() => {
                      if (!item.subItems) setIsMobileMenuOpen(false);
                    }}
                  >
                    {item.label}
                    {item.id === "infoway" && <MapleLeaf size={24} />}
                  </Link>
                  {item.subItems && item.subItems.length > 0 && (
                    <div className={styles.SubMenuList}>
                      {item.subItems.map((sub) => (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={cx(styles.MobileMenuLink, {
                            [styles.MobileMenuLinkDark]: darkMode
                          })}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className={styles.MobileMenuButtons}>
              <Button
                variant="outline"
                dark={darkMode}
                onClick={() => {
                  window.location.href = APP_URL;
                }}
              >
                Login
              </Button>
              <TryMikaButton dark={darkMode} inviteCode={inviteCode} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;
